import { combineLatest, map, type Observable } from 'rxjs';
import {
  type APIFieldAttributes,
  type APIModelAttributes,
  type APIReferenceAttributes,
  type APITagAttributes,
  ArdoqId,
  type LoadedState,
  type MetaModelTriple,
  type TraversalLoadedState,
} from '@ardoq/api-types';
import { fields$ } from '../fields/fields$';
import { getWorkspaceIdsAndTypeNamesFromTraversalPaths } from './getWorkspaceIdsAndTypeNamesFromTraversalPaths';
import { isViewpointMode$ } from 'traversals/loadedGraph$';
import { loadedState$ } from 'loadedState/loadedState$';
import { componentInterface } from '@ardoq/component-interface';
import { metaModelAsScopeData$ } from 'viewpointBuilder/metaModel/loadMetaModelAsScopeData$';
import { tags$, tagLoadingStatus$ } from '../tags/tags$';
import { references$ } from '../references/references$';
import { models$ } from '../models/models$';
import { isDefaultWildcardTraversal } from 'traverseOneLevel/defaultWildcardViewpoint';
import { anyType } from '@ardoq/global-consts';
import { isTraversalWithPaths } from 'loadedState/isTraversalWithPaths';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { isEqual, partition } from 'lodash';

type OptionsSourceData = {
  componentTypeNamesSet: Set<string>;
  referenceTypeNamesSet: Set<string>;
  workspaceIdsSet: Set<string>;
  availableComponentFields: APIFieldAttributes[];
  availableReferenceFields: APIFieldAttributes[];
};

type OptionsSourceDataReducerState = OptionsSourceData & {
  metaModelAsScopeData: {
    componentNameAndFieldsIndex: Map<string, string[]>;
    componentNameAndWorkspacesIndex: Map<string, string[]>;
    referenceNameAndFieldsIndex: Map<string, string[]>;
    triples: MetaModelTriple[];
  };
  fieldMap: Map<string, APIFieldAttributes>;
  hasWildcardTraversal: boolean;
};

const addOptionsSourceDataForTraversal = (
  optionsSourceDataReducerState: OptionsSourceDataReducerState,
  entry: TraversalLoadedState
) => {
  const {
    componentTypeNamesSet,
    referenceTypeNamesSet,
    workspaceIdsSet,
    availableComponentFields,
    availableReferenceFields,
    fieldMap,
    metaModelAsScopeData: {
      componentNameAndFieldsIndex,
      componentNameAndWorkspacesIndex,
      referenceNameAndFieldsIndex,
      triples,
    },
  } = optionsSourceDataReducerState;

  const {
    componentTypeNames,
    referenceTypeNames,
    workspaceIds,
    availableComponentFields: entryAvailableComponentFields,
    availableReferenceFields: entryAvailableReferenceFields,
  } = getWorkspaceIdsAndTypeNamesFromTraversalPaths({
    paths: entry.data.paths,
    componentNameAndFieldsIndex,
    componentNameAndWorkspacesIndex,
    referenceNameAndFieldsIndex,
    fieldMap,
    triples,
  });
  componentTypeNames.forEach(componentTypeNamesSet.add, componentTypeNamesSet);
  referenceTypeNames.forEach(referenceTypeName => {
    // Wildcard traversals use `anyType` as a placeholder reference type. The
    // actual reference types present in the view are derived from the loaded
    // references later (see addReferenceTypeNamesFromLoadedReferences).
    if (referenceTypeName !== anyType) {
      referenceTypeNamesSet.add(referenceTypeName);
    }
  });
  workspaceIds.forEach(workspaceIdsSet.add, workspaceIdsSet);
  entryAvailableComponentFields.forEach(field =>
    availableComponentFields.push(field)
  );
  entryAvailableReferenceFields.forEach(field =>
    availableReferenceFields.push(field)
  );
  entry.data.pathCollapsingRules.forEach(({ displayText }) =>
    referenceTypeNamesSet.add(displayText)
  );
  if (isDefaultWildcardTraversal(entry.data.paths)) {
    optionsSourceDataReducerState.hasWildcardTraversal = true;
  }
  return optionsSourceDataReducerState;
};

const addOptionsSourceDataFromLoadedComponents = (
  optionsSourceDataReducerState: OptionsSourceDataReducerState,
  entry: LoadedState
) => {
  const {
    componentTypeNamesSet,
    workspaceIdsSet,
    availableComponentFields,
    fieldMap,
    metaModelAsScopeData: {
      componentNameAndFieldsIndex,
      componentNameAndWorkspacesIndex,
    },
  } = optionsSourceDataReducerState;

  const componentIds = entry.componentIdsAndReferences?.componentIds;
  componentIds?.forEach(componentId => {
    const componentTypeName = componentInterface.getType(componentId)?.name;
    if (!componentTypeName) {
      return;
    }

    const workspaceIds = componentNameAndWorkspacesIndex.get(componentTypeName);
    const fields = componentNameAndFieldsIndex.get(componentTypeName);

    componentTypeNamesSet.add(componentTypeName);
    workspaceIds?.forEach(workspaceIdsSet.add, workspaceIdsSet);
    fields?.forEach(fieldId => {
      const field = fieldMap.get(fieldId);
      if (field) {
        availableComponentFields.push(field);
      }
    });
  });
  return optionsSourceDataReducerState;
};

const getLoadedStatesSeparatedByPaths = (loadedStates: LoadedState[]) => {
  const [statesWithPaths, statesWithoutPaths] = partition(
    loadedStates,
    isTraversalWithPaths
  );

  return {
    statesWithPaths,
    statesWithoutPaths,
  };
};

/**
 * For wildcard traversals the paths only contain the `anyType` placeholder for
 * the reference type, so we cannot derive concrete reference type names from
 * the traversal definition. Instead, look at the references that are actually
 * loaded into the view and resolve their type names via the model that owns
 * them. This keeps the "By Reference" grouping option enabled when the view
 * contains references, and limits the available options to types that
 * actually have instances.
 */
const getReferenceTypeName = (
  reference: APIReferenceAttributes,
  modelsById: Record<ArdoqId, APIModelAttributes>
): string | undefined =>
  modelsById[reference.model]?.referenceTypes?.[reference.type]?.name;

export const addReferenceTypeNamesFromLoadedReferences = (
  referenceTypeNamesSet: Set<string>,
  references: APIReferenceAttributes[],
  modelsById: Record<ArdoqId, APIModelAttributes>
) => {
  references.forEach(reference => {
    const referenceTypeName = getReferenceTypeName(reference, modelsById);
    if (referenceTypeName) {
      referenceTypeNamesSet.add(referenceTypeName);
    }
  });
};

export type ViewpointOptionsSourceData = {
  workspaceIds: ArdoqId[];
  componentTypeNames: string[];
  referenceTypeNames: string[];
  availableComponentFields: APIFieldAttributes[];
  availableReferenceFields: APIFieldAttributes[];
  tags: APITagAttributes[];
};

export const viewpointOptionsSourceData$: Observable<ViewpointOptionsSourceData | null> =
  combineLatest({
    loadedStates: loadedState$,
    rawFields: fields$,
    isViewpointMode: isViewpointMode$,
    metaModelAsScopeData: metaModelAsScopeData$,
    tags: tags$,
    tagLoadingStatus: tagLoadingStatus$,
    references: references$,
    models: models$,
  }).pipe(
    map(
      ({
        loadedStates: { loadedStates },
        rawFields: { byId },
        isViewpointMode: { isViewpointMode },
        metaModelAsScopeData,
        tags,
        tagLoadingStatus,
        references,
        models,
      }) => {
        if (
          !isViewpointMode ||
          metaModelAsScopeData.status !== 'DATA_LOADED' ||
          tagLoadingStatus.fetchingStatus !== 'success'
        ) {
          return null;
        }

        const fieldMap = new Map(
          Object.values(byId).map(field => [field.name, field])
        );

        const groupingOptionsSourceDataReducerState: OptionsSourceDataReducerState =
          {
            componentTypeNamesSet: new Set<string>(),
            referenceTypeNamesSet: new Set<string>(),
            workspaceIdsSet: new Set<string>(),
            availableComponentFields: [] as APIFieldAttributes[],
            availableReferenceFields: [] as APIFieldAttributes[],
            metaModelAsScopeData,
            fieldMap,
            hasWildcardTraversal: false,
          };

        const { statesWithPaths, statesWithoutPaths } =
          getLoadedStatesSeparatedByPaths(loadedStates);

        const traversalStatesOptionsData = statesWithPaths.reduce(
          addOptionsSourceDataForTraversal,
          groupingOptionsSourceDataReducerState
        );

        const allStatesOptionsData = statesWithoutPaths.reduce(
          addOptionsSourceDataFromLoadedComponents,
          traversalStatesOptionsData
        );

        const {
          componentTypeNamesSet,
          referenceTypeNamesSet,
          workspaceIdsSet,
          availableComponentFields,
          availableReferenceFields,
          hasWildcardTraversal,
        } = allStatesOptionsData;

        if (hasWildcardTraversal) {
          addReferenceTypeNamesFromLoadedReferences(
            referenceTypeNamesSet,
            references.models,
            models.byId
          );
        }

        return {
          workspaceIds: Array.from(workspaceIdsSet),
          componentTypeNames: Array.from(componentTypeNamesSet),
          referenceTypeNames: Array.from(referenceTypeNamesSet),
          tags: tags.models,
          availableComponentFields,
          availableReferenceFields,
        };
      }
    ),
    startWith(null),
    distinctUntilChanged(isEqual)
  );
